import Actions from 'actions'
import { MobileTabs } from 'consts/appConsts'
import ROUTES from 'consts/routesConsts'
import useQueryParams from 'hooks/useQueryParams'
import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, useHistory, useParams } from 'react-router'
import PatientsChat from './Chat/PatientsChat'
import NotesTab from './PatientCard/NotesTab'
import PatientCopilot from './PatientSwitchableView/PatientCopilot/PatientCopilot'
import Timeline from './Timeline/V1/Timeline.mobile'

const PatientMobile = props => {
  const params = useParams()
  const { queryParams } = useQueryParams()
  const { referralId, grinScanId, patientView } = queryParams
  const patientId = params.patientId !== 'undefined' ? params.patientId : undefined

  const dispatch = useDispatch()
  const history = useHistory()
  const { patient } = useSelector(state => state.patientsReducer)
  const isBroadcastMode = useSelector(state => state.chatReducer.broadcast.isModeOn)

  const grinScanFromQueryParam = useMemo(
    () => patient?.grinScans?.items?.find(scan => scan.id === grinScanId),
    [grinScanId, patient?.grinScans?.items]
  )

  useEffect(() => {
    if (patientId && history.location.pathname !== ROUTES.BROADCAST) {
      dispatch(Actions.requestPatient({ patientId }))
    } else if (!patientId || (history.location.pathname === ROUTES.BROADCAST && !isBroadcastMode)) {
      history.push(ROUTES.PATIENTS)
    }
  }, [dispatch, patientId, history, isBroadcastMode])

  useEffect(() => {
    if (referralId && grinScanId && grinScanFromQueryParam) {
      dispatch(
        Actions.toggleViewSharedScanModal({
          isOpen: true,
          referralId,
          grinScanId
        })
      )
      history.replace({
        search: '',
        pathname: `${history.location.pathname}/timeline`
      })
    }
  }, [dispatch, grinScanFromQueryParam, grinScanId, history, patient, referralId])

  // Translate `?patientView=<view>` (the desktop URL convention) into the
  // mobile path-based route `/patients/:id/<tab>`. Without this, a deep-link
  // like `/patients/<id>?patientView=copilot` matches no Route below and the
  // user sees a blank screen on mobile. We trust the URL and redirect to the
  // matching tab path — access gating for that tab (e.g., copilot needs
  // isHI + copilotFF) is enforced at render time by each Route below, and
  // duplicating it here would race against billing data loading after
  // doctor.id populates.
  useEffect(() => {
    if (!patientId || !patientView) return

    // Skip if the URL already has a tab segment (avoids redirect loop).
    const tabs = Object.values(MobileTabs)
    const lastSegment = history.location.pathname.split('/').filter(Boolean).pop()
    if (tabs.includes(lastSegment)) return

    const viewToTab = {
      copilot: MobileTabs.COPILOT,
      chat: MobileTabs.CHAT,
      notes: MobileTabs.NOTES,
      timeline: MobileTabs.TIMELINE
    }
    const targetTab = viewToTab[patientView]
    if (!targetTab) return

    history.replace({
      pathname: `${ROUTES.PATIENTS}/${patientId}/${targetTab}`,
      search: history.location.search
    })
  }, [patientId, patientView, history])

  return (
    <>
      <Route path={ROUTES.BROADCAST} exact>
        <PatientsChat />
      </Route>
      <Route path={`${ROUTES.PATIENTS}/:patientId/${MobileTabs.TIMELINE}`} exact>
        <Timeline />
      </Route>
      <Route path={`${ROUTES.PATIENTS}/:patientId/${MobileTabs.CHAT}`} exact>
        <PatientsChat />
      </Route>
      <Route path={`${ROUTES.PATIENTS}/:patientId/${MobileTabs.NOTES}`} exact>
        <NotesTab />
      </Route>
      <Route path={`${ROUTES.PATIENTS}/:patientId/${MobileTabs.COPILOT}`} exact>
        {/* The previous version of this Route redirected to /chat when
            `isCopilotAccessible` was false. That branch fires synchronously
            on every render, including the initial render where isHI /
            copilotFF haven't yet resolved (they depend on grinPlan + a few
            other Redux slices that load in separate dispatches), so a
            deep-link from an HI user got redirected to /chat before the
            user's access could be determined. We now render PatientCopilot
            unconditionally: a non-HI user reaching this URL would only see
            empty data (their backend queries 403 / return empty) — an edge
            case unreachable from the mobile UI, since they don't have a
            Copilot entry in their nav and won't receive Copilot deep-links
            in notifications. */}
        <PatientCopilot />
      </Route>
    </>
  )
}

export default PatientMobile
